import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { api } from '@/utils/api';
// Badge reaudit R9: calendar-date formatting (no day-shift on date-only JHA dates).
import { formatCalendarDate } from '@/utils/calendarDate';

interface JHA {
  id: number;
  completionTime?: number | null;
  date: string;
  projectId: number;
  project: {
    name: string;
    siteAddress: string;
  };
  createdBy: {
    firstName: string;
    lastName: string;
  };
  crewMembers: any;
  hazards: any;
  signedAt: string;
  weather: any;
  // S7: set when a worker created this JHA in an emergency — foreman review pending
  needsForemanReview?: boolean;
}

export function JHAPage() {
  const [jhas, setJhas] = useState<JHA[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadJHAs();
  }, [filter]);

  const loadJHAs = async () => {
    try {
      const response = await api.get('/jha');
      setJhas(response.data.data || []);
    } catch (error) {
      console.error('Error loading JHAs:', error);
    } finally {
      setLoading(false);
    }
  };

  // BP2: real calendar-date filtering — Today / This Week (Mon–Sun) / This
  // Month. Auditor fix: JHA dates are stored as UTC midnights of calendar
  // dates, so compare UTC y/m/d PARTS (never timezone-round-trip a calendar
  // date — same rule as utils/calendarDate.ts); local getters excluded a
  // site's own today JHA in negative-UTC timezones.
  const utcParts = (d: Date) => ({ y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate(), dow: (d.getUTCDay() + 6) % 7 });
  const daysFromUtcMidnight = (d: Date) => Math.floor(d.getTime() / 86400000);
  const isToday = (d: Date) => {
    const a = utcParts(d), b = utcParts(new Date());
    return a.y === b.y && a.m === b.m && a.d === b.d;
  };
  const isThisWeek = (d: Date) => {
    // Monday of the current week in UTC-day numbers.
    const now = new Date();
    const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    const m = Math.floor(monday.getTime() / 86400000);
    const day = daysFromUtcMidnight(d);
    return day >= m && day < m + 7;
  };
  const isThisMonth = (d: Date) => {
    const a = utcParts(d), b = utcParts(new Date());
    return a.y === b.y && a.m === b.m;
  };

  const filteredJHAs = jhas.filter((jha) => {
    const matchesSearch =
      jha.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      jha.project.siteAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${jha.createdBy.firstName} ${jha.createdBy.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());

    const jhaDate = new Date(jha.date);
    if (filter === 'today') return matchesSearch && isToday(jhaDate);
    if (filter === 'week') return matchesSearch && isThisWeek(jhaDate);
    if (filter === 'month') return matchesSearch && isThisMonth(jhaDate);
    return matchesSearch;
  });

  // BP2: real derived metrics — no invented "12 min saved".
  const todayCount = jhas.filter(j => isToday(new Date(j.date))).length;
  const weekCount = jhas.filter(j => isThisWeek(new Date(j.date))).length;
  const completionTimes = jhas.map(j => j.completionTime).filter((t: any) => Number.isFinite(t) && (t as number) > 0) as number[];
  const avgCompletion = completionTimes.length
    ? Math.round(completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length)
    : null;

  const getCrewSize = (crewMembers: any) => {
    if (Array.isArray(crewMembers)) return crewMembers.length;
    return 0;
  };

  const getHazardCount = (hazards: any) => {
    if (Array.isArray(hazards)) return hazards.length;
    if (hazards && typeof hazards === 'object') {
      return (hazards.physical?.length ?? 0) + (hazards.health?.length ?? 0);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <ClipboardCheck className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Loading assessments...</p>
          </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 flex-shrink-0">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3 truncate">
                  <ClipboardCheck className="h-8 w-8 text-blue-600 flex-shrink-0" />
                  <span className="truncate">Pre-Work Hazard Assessments</span>
                </h1>
                <p className="text-gray-600 mt-1 truncate">Daily safety briefings and hazard assessments per ANSI standards</p>
              </div>
            </div>
            <Link
              to="/jha/create"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 font-semibold"
            >
              <Plus className="h-5 w-5" />
              New Assessment
            </Link>
          </div>

          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by project, site, or foreman..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All JHAs</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container mx-auto px-6 py-6">
          <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Assessments</p>
                <p className="text-3xl font-bold text-gray-900">{jhas.length}</p>
              </div>
              <ClipboardCheck className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Today</p>
                <p className="text-3xl font-bold text-green-600">{todayCount}</p>
              </div>
              <Calendar className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">This Week</p>
                <p className="text-3xl font-bold text-purple-600">{weekCount}</p>
              </div>
              <Clock className="h-12 w-12 text-purple-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Completion Time</p>
                {/* BP2: real metric from actual recorded assessments — never invented. */}
                <p className="text-2xl font-bold text-orange-600">{avgCompletion != null ? `${avgCompletion} min` : '—'}</p>
                <p className="text-xs text-gray-500">{avgCompletion != null ? 'recorded on completed assessments' : 'recorded once assessments capture completion time'}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* JHA List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project / Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Foreman
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Crew Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hazards
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJHAs.map((jha) => (
                  <tr key={jha.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatCalendarDate(jha.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{jha.project.name}</div>
                      <div className="text-sm text-gray-500">{jha.project.siteAddress}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {jha.createdBy.firstName} {jha.createdBy.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-900">
                        <Users className="h-4 w-4 text-gray-400" />
                        {getCrewSize(jha.crewMembers)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-orange-600 font-medium">
                        <AlertTriangle className="h-4 w-4" />
                        {getHazardCount(jha.hazards)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* BP2: status derives from the server-set review flag —
                          a JHA awaiting foreman review is NOT "Signed". */}
                      {jha.needsForemanReview ? (
                        <span
                          className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800"
                          data-testid="needs-review-badge"
                          title="Awaiting foreman review"
                        >
                          Awaiting Review
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Signed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-3 text-sm">
                        <Link
                          to={`/jha/${jha.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View Details
                        </Link>
                        {/* WD-T1: 'Copy to create new' — opens the create flow
                            prefilled from this JHA (source is read-only). */}
                        <Link
                          to={`/jha/create?copyFrom=${jha.id}`}
                          data-testid={`copy-jha-${jha.id}`}
                          className="text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Copy to create new
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredJHAs.length === 0 && (
              // DEMO-QA re-triage: filter-aware zero-results copy — a narrowed
              // list (filter or search excluded everything) is NOT the same as
              // "no data at all".
              jhas.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No assessments found</p>
                  <Link
                    to="/jha/create"
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Create a new Pre-Work Hazard Assessment
                  </Link>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ClipboardCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    {searchTerm.trim()
                      ? 'No assessments match your search'
                      : 'No assessments match this filter'}
                  </p>
                  <Link
                    to="/jha/create"
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Create a new assessment
                  </Link>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

